'use client';

import { useEffect, useState } from 'react';

import { consentStorageAdapter } from '../adapters';
import { getConsentStatusFactory } from '../domain/get-consent-status';
import { ConsentStatus } from '../domain/types';
import type { ConsentChoice } from '../domain/types';
import { useConsentContext } from './consent-context-provider';

export interface UseConsentReturn {
  status: ConsentStatus;
  isLoading: boolean;
  saveConsent: (choice: ConsentChoice) => void;
  clearConsent: () => void;
}

export function useConsent(): UseConsentReturn {
  const { requiresConsent, initialStatus } = useConsentContext();

  const [status, setStatus] = useState<ConsentStatus>(() => {
    if (initialStatus && initialStatus !== ConsentStatus.UNKNOWN) {
      return initialStatus;
    }

    return ConsentStatus.UNKNOWN;
  });

  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return status === ConsentStatus.UNKNOWN;
  });

  useEffect(() => {
    const loadConsentStatus = async () => {
      try {
        const getConsentStatus = getConsentStatusFactory({
          storage: consentStorageAdapter,
          requiresConsent,
        });

        const result = await getConsentStatus();

        if (result.success) {
          setStatus(result.data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadConsentStatus();
  }, [requiresConsent]);

  const saveConsent = (choice: ConsentChoice) => {
    consentStorageAdapter.saveConsent(choice);
    setStatus(
      choice === 'all_cookies'
        ? ConsentStatus.ALL_COOKIES
        : ConsentStatus.REQUIRED_COOKIES,
    );
  };

  const clearConsent = () => {
    consentStorageAdapter.clearConsent();
    setStatus(ConsentStatus.UNKNOWN);
  };

  return {
    status,
    isLoading,
    saveConsent,
    clearConsent,
  };
}
